/**
 * PSP/CRE Tool v2.1 - CBRN Medical Constants
 * 
 * Defines Chemical, Biological, Radiological, and Nuclear (CBRN)
 * medical casualty rates, decontamination procedures, and treatment protocols.
 * 
 * @fileoverview CBRN medical planning factors and treatment protocols
 * @module constants/cbrnMedical
 * @author Dr. Tatar Radu MD - Romanian Military Medical Institute
 * @date December 22, 2024
 * @version 2.1 - icons.js Integration
 * 
 * Key CBRN Medical Principles:
 * 1. Decontamination BEFORE treatment (except life-saving)
 * 2. PPE reduces medical capability by 30-50%
 * 3. Antidote timing is critical for nerve agents
 * 4. Mass casualty events are the norm, not exception
 * 5. Contaminated casualties create facility contamination risk
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (STATUS_ICONS, MEDICAL_ICONS, TACTICAL_ICONS)
 * - Added getChemicalAgentOptions(), getBioAgentOptions(), getThreatLevelOptions()
 * - Added validateCBRNConfig() with enhanced validation
 * - Enhanced Ukraine 2022-2024 context
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  MEDICAL_ICONS,
  TACTICAL_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  BLACK: STATUS_ICONS?.BLACK || '⚫',
  
  // Medical icons
  BIOHAZARD: MEDICAL_ICONS?.BIOHAZARD || '☣️',
  RADIATION: MEDICAL_ICONS?.RADIATION || '☢️',
  SKULL: MEDICAL_ICONS?.SKULL || '☠️',
  SYRINGE: MEDICAL_ICONS?.SYRINGE || '💉',
  
  // Tactical icons
  WARNING: TACTICAL_ICONS?.WARNING || '⚠️',
  SHIELD: TACTICAL_ICONS?.SHIELD || '🛡️'
};

// ============================================================================
// CBRN THREAT LEVELS
// ============================================================================

// CBRN DNBI multiplier sources:
// AJMedP-7.5 Table 3 — MOPP-condition DNBI elevation factors.
// QM-6I-001 fix: STANAG 2226 is NATO Military Police Guidance (ATP-3.7.2) — unrelated to CBRN medicine.
// Correct reference: STANAG 2553 (AMedP-7.5) — NATO Planning Guide for Estimation of CBRN Casualties.
// Mechanisms: MOPP suit heat stress, dehydration, psychological burden, secondary contamination casualties.
export const CBRN_THREAT_LEVELS = {
  NONE: {
    code: 'NONE',
    label: 'No CBRN Threat',
    icon: ICONS.GREEN,
    color: '#4CAF50',
    mopp_level: 0,
    medical_impact: 1.0,
    // No CBRN overhead; DNBI baseline unaffected.
    dnbi_multiplier: 1.00,
    educational: {
      key_point: 'Normal operations - no CBRN considerations'
    }
  },
  LOW: {
    code: 'LOW',
    label: 'Low CBRN Threat',
    icon: ICONS.YELLOW,
    color: '#8BC34A',
    mopp_level: 0,
    medical_impact: 0.95,
    // MOPP 0 with surveillance burden; minimal heat stress but heightened vigilance fatigue.
    dnbi_multiplier: 1.15,
    educational: {
      key_point: 'MOPP gear available, not donned'
    }
  },
  MODERATE: {
    code: 'MODERATE',
    label: 'Moderate CBRN Threat',
    icon: ICONS.ORANGE,
    color: '#FF9800',
    mopp_level: 2,
    medical_impact: 0.75,
    // MOPP 2 — suit discomfort, heat accumulation, limited hydration; +35% DNBI elevation.
    dnbi_multiplier: 1.35,
    educational: {
      key_point: 'MOPP 2 - overgarment and mask available'
    }
  },
  HIGH: {
    code: 'HIGH',
    label: 'High CBRN Threat',
    icon: ICONS.RED,
    color: '#F44336',
    mopp_level: 4,
    medical_impact: 0.50,
    // MOPP 4 — severe heat stress, dehydration, secondary contamination casualties; +65% DNBI elevation.
    dnbi_multiplier: 1.65,
    educational: {
      key_point: 'MOPP 4 - full protective posture, 50% medical capacity'
    }
  },
  IMMINENT: {
    code: 'IMMINENT',
    label: 'Imminent CBRN Attack',
    icon: ICONS.BLACK,
    color: '#000000',
    mopp_level: 4,
    medical_impact: 0.30,
    // Active CBRN event — contamination casualties added to DNBI stream; psychological burden acute; x2.0 DNBI.
    dnbi_multiplier: 2.00,
    educational: {
      key_point: 'Active attack or contamination present'
    }
  }
};

// ============================================================================
// CHEMICAL AGENT CATEGORIES
// ============================================================================

export const CHEMICAL_AGENTS = {
  NERVE: {
    category: 'NERVE',
    label: 'Nerve Agents',
    shortLabel: 'NA',
    nato_symbol: 'G/V',
    description: 'Organophosphate compounds affecting nervous system',
    icon: ICONS.SKULL,
    color: '#F44336',
    priority: 1,
    
    agents: {
      GA_TABUN: {
        name: 'Tabun',
        nato_code: 'GA',
        icon: ICONS.SKULL,
        physical: {
          state: 'LIQUID',
          color: 'Colorless to brownish',
          odor: 'Fruity',
          persistence_hours: 24,
          vapor_hazard: 'MODERATE'
        },
        toxicity: {
          lclo_mg_min_m3: 400,
          ld50_dermal_mg: 14000,
          iclo_mg_min_m3: 70
        },
        onset_minutes: { vapor: 0.5, liquid: 5 },
        lethality_untreated: 0.50,
        route: ['INHALATION', 'DERMAL', 'OCULAR'],
        aging_hours: 14,
        notes: 'First nerve agent. Distinctive fruity smell.'
      },
      
      GB_SARIN: {
        name: 'Sarin',
        nato_code: 'GB',
        icon: ICONS.SKULL,
        physical: {
          state: 'LIQUID',
          color: 'Colorless',
          odor: 'None to slight',
          persistence_hours: 1,
          vapor_hazard: 'HIGH'
        },
        toxicity: {
          lclo_mg_min_m3: 70,
          ld50_dermal_mg: 1700,
          iclo_mg_min_m3: 25
        },
        onset_minutes: { vapor: 0.5, liquid: 30 },
        lethality_untreated: 0.70,
        route: ['INHALATION'],
        aging_hours: 5,
        notes: 'Highly volatile. Syria conflict. Primary inhalation threat.',
        ukraine_relevance: 'Assessed as credible threat in Ukraine conflict'
      },
      
      GD_SOMAN: {
        name: 'Soman',
        nato_code: 'GD',
        icon: ICONS.RED,
        physical: {
          state: 'LIQUID',
          color: 'Colorless',
          odor: 'Fruity/camphor',
          persistence_hours: 48,
          vapor_hazard: 'MODERATE'
        },
        toxicity: {
          lclo_mg_min_m3: 70,
          ld50_dermal_mg: 350,
          iclo_mg_min_m3: 25
        },
        onset_minutes: { vapor: 0.5, liquid: 2 },
        lethality_untreated: 0.85,
        route: ['INHALATION', 'DERMAL'],
        aging_minutes: 2,
        notes: 'CRITICAL: Ages in 2 minutes - antidote within seconds!'
      },
      
      VX: {
        name: 'VX',
        nato_code: 'VX',
        icon: ICONS.BLACK,
        physical: {
          state: 'OILY_LIQUID',
          color: 'Amber',
          odor: 'None',
          persistence_days: 21,
          vapor_hazard: 'LOW'
        },
        toxicity: {
          lclo_mg_min_m3: 10,
          ld50_dermal_mg: 10,
          iclo_mg_min_m3: 3
        },
        onset_minutes: { vapor: 0.5, liquid: 180 },
        lethality_untreated: 0.95,
        route: ['DERMAL'],
        aging_hours: 48,
        notes: 'Most toxic nerve agent. Persistent. Primary skin hazard.'
      },
      
      NOVICHOK: {
        name: 'Novichok-class',
        nato_code: 'A-series',
        icon: ICONS.BLACK,
        physical: {
          state: 'POWDER/LIQUID',
          color: 'Variable',
          odor: 'Unknown',
          persistence_days: 14,
          vapor_hazard: 'UNKNOWN'
        },
        toxicity: {
          lclo_mg_min_m3: 5,
          ld50_dermal_mg: 1,
          iclo_mg_min_m3: 1
        },
        onset_minutes: { vapor: 0.5, liquid: 30 },
        lethality_untreated: 0.98,
        route: ['DERMAL', 'INHALATION'],
        aging_hours: 12,
        treatment_notes: 'Standard antidotes may be less effective',
        notes: 'Fourth generation. Salisbury 2018.',
        ukraine_relevance: 'Russian origin - credible threat'
      }
    },
    
    clinical_presentation: {
      MILD: {
        exposure_level: 'LOW',
        symptoms: ['Miosis (pinpoint pupils)', 'Rhinorrhea', 'Mild dyspnea', 'Salivation'],
        consciousness: 'ALERT',
        respiratory: 'ADEQUATE',
        treatment_urgency: 'DELAYED',
        icon: ICONS.YELLOW
      },
      MODERATE: {
        exposure_level: 'MODERATE',
        symptoms: ['Severe miosis', 'Bronchorrhea', 'Vomiting', 'Fasciculations', 'Weakness'],
        consciousness: 'CONFUSED',
        respiratory: 'LABORED',
        treatment_urgency: 'URGENT',
        icon: ICONS.ORANGE
      },
      SEVERE: {
        exposure_level: 'HIGH',
        symptoms: ['Seizures', 'Paralysis', 'Respiratory arrest', 'Unconsciousness'],
        consciousness: 'UNCONSCIOUS',
        respiratory: 'APNEIC',
        treatment_urgency: 'IMMEDIATE',
        icon: ICONS.RED
      }
    },
    
    treatment: {
      antidotes: {
        ATROPINE: {
          name: 'Atropine Sulfate',
          icon: ICONS.SYRINGE,
          mechanism: 'Muscarinic antagonist',
          dosing: {
            mild: { dose_mg: 2, route: 'IM/IV', repeat_minutes: 5, max_doses: 3 },
            moderate: { dose_mg: 4, route: 'IM/IV', repeat_minutes: 5, max_doses: 5 },
            severe: { dose_mg: 6, route: 'IV', repeat_minutes: 3, max_doses: 'Until dry' }
          },
          endpoint: 'Dry secretions (NOT pupil dilation)',
          notes: 'May need 20-40mg total in severe cases'
        },
        PRALIDOXIME: {
          name: 'Pralidoxime Chloride (2-PAM)',
          icon: ICONS.SYRINGE,
          mechanism: 'Reactivates acetylcholinesterase before aging',
          dosing: {
            all_severities: { dose_mg: 1000, route: 'IV over 15-30min', repeat_hours: 1 }
          },
          critical_timing: 'MUST give before aging',
          notes: 'Ineffective after aging - timing critical for GD (2 min!)'
        },
        DIAZEPAM: {
          name: 'Diazepam',
          icon: ICONS.SYRINGE,
          mechanism: 'Anticonvulsant',
          dosing: {
            seizures: { dose_mg: 10, route: 'IM/IV', repeat_minutes: 10, max_doses: 3 }
          },
          notes: 'For seizures only'
        }
      },
      
      autoinjectors: {
        ATNAA: { name: 'ATNAA', contents: 'Atropine 2.1mg + 2-PAM 600mg', icon: ICONS.SYRINGE },
        CANA: { name: 'CANA', contents: 'Diazepam 10mg', icon: ICONS.SYRINGE }
      }
    },
    
    educational: {
      key_point: 'SLUDGE: Salivation, Lacrimation, Urination, Defecation, GI, Emesis',
      teaching_note: 'Atropine endpoint is DRY SECRETIONS not pupil size',
      common_mistake: 'Stopping atropine because pupils dilated'
    }
  },
  
  BLISTER: {
    category: 'BLISTER',
    label: 'Blister Agents (Vesicants)',
    shortLabel: 'BA',
    nato_symbol: 'H/L',
    description: 'Cause blistering of skin and mucous membranes',
    icon: '🔥',
    color: '#FF5722',
    priority: 2,
    
    agents: {
      HD_MUSTARD: {
        name: 'Sulfur Mustard',
        nato_code: 'HD',
        icon: '🔥',
        physical: {
          state: 'OILY_LIQUID',
          color: 'Yellow to brown',
          odor: 'Garlic/mustard',
          persistence_days: 7,
          vapor_hazard: 'MODERATE'
        },
        onset_hours: { symptoms: 2, blisters: 12 },
        lethality_untreated: 0.05,
        route: ['DERMAL', 'INHALATION', 'OCULAR'],
        notes: 'DELAYED symptoms - treat all exposed even if asymptomatic'
      },
      L_LEWISITE: {
        name: 'Lewisite',
        nato_code: 'L',
        icon: '🔥',
        physical: {
          state: 'OILY_LIQUID',
          color: 'Colorless to violet',
          odor: 'Geranium',
          persistence_hours: 24,
          vapor_hazard: 'HIGH'
        },
        onset_minutes: { pain: 0.5, blisters: 720 },
        lethality_untreated: 0.10,
        route: ['DERMAL', 'INHALATION'],
        antidote: 'BAL (Dimercaprol)',
        notes: 'Immediate pain (unlike mustard)'
      }
    },
    
    treatment: {
      decontamination: 'Within 2 minutes prevents most injury',
      supportive: ['Copious irrigation', 'Burn care protocols', 'Pain management'],
      monitoring: 'Bone marrow suppression at days 7-14',
      notes: 'Blister fluid is NOT contaminated'
    },
    
    educational: {
      key_point: 'Decon within 2 minutes prevents most injury',
      teaching_note: 'Blister fluid is safe to handle',
      common_mistake: 'Missing delayed-onset casualties'
    }
  },
  
  BLOOD: {
    category: 'BLOOD',
    label: 'Blood Agents (Cyanides)',
    shortLabel: 'AC',
    nato_symbol: 'AC/CK',
    description: 'Interfere with cellular oxygen utilization',
    icon: '💀',
    color: '#9C27B0',
    priority: 3,
    
    agents: {
      AC_HCN: {
        name: 'Hydrogen Cyanide',
        nato_code: 'AC',
        icon: '💀',
        physical: {
          state: 'LIQUID/GAS',
          color: 'Colorless',
          odor: 'Bitter almonds (40% cannot smell)',
          persistence_minutes: 30,
          vapor_hazard: 'HIGH'
        },
        onset_seconds: { vapor: 15 },
        lethality_untreated: 0.85,
        route: ['INHALATION'],
        notes: 'Rapid onset - death in minutes if severe'
      },
      CK: {
        name: 'Cyanogen Chloride',
        nato_code: 'CK',
        icon: '💀',
        physical: {
          state: 'GAS',
          color: 'Colorless',
          odor: 'Pungent',
          persistence_minutes: 15,
          vapor_hazard: 'HIGH'
        },
        onset_seconds: { vapor: 30 },
        lethality_untreated: 0.80,
        route: ['INHALATION'],
        notes: 'Also causes lacrimation and pulmonary irritation'
      }
    },
    
    treatment: {
      antidotes: {
        HYDROXOCOBALAMIN: {
          name: 'Hydroxocobalamin (Cyanokit)',
          dosing: { dose_g: 5, route: 'IV over 15min', repeat: true },
          mechanism: 'Binds cyanide to form cyanocobalamin',
          preferred: true,
          notes: 'Preferred antidote - safe even if not cyanide'
        },
        NITRITE_KIT: {
          name: 'Cyanide Antidote Kit',
          contents: ['Amyl nitrite', 'Sodium nitrite', 'Sodium thiosulfate'],
          warning: 'Do NOT use in smoke inhalation (methemoglobin risk)'
        }
      }
    },
    
    educational: {
      key_point: 'Hydroxocobalamin is preferred - safe even if wrong diagnosis',
      teaching_note: '40% of population cannot smell bitter almonds',
      common_mistake: 'Using nitrite kit in smoke inhalation victims'
    }
  },
  
  CHOKING: {
    category: 'CHOKING',
    label: 'Choking Agents (Pulmonary)',
    shortLabel: 'CG',
    nato_symbol: 'CG',
    description: 'Damage respiratory tract and cause pulmonary edema',
    icon: '💨',
    color: '#607D8B',
    priority: 4,
    
    agents: {
      CG_PHOSGENE: {
        name: 'Phosgene',
        nato_code: 'CG',
        icon: '💨',
        physical: {
          state: 'GAS',
          color: 'Colorless',
          odor: 'Fresh-cut hay/grass',
          persistence_minutes: 30,
          vapor_hazard: 'HIGH'
        },
        onset_hours: { pulmonary_edema: 24 },
        lethality_untreated: 0.40,
        route: ['INHALATION'],
        notes: 'DELAYED pulmonary edema - enforced rest critical'
      },
      CL_CHLORINE: {
        name: 'Chlorine',
        nato_code: 'CL',
        icon: '💨',
        physical: {
          state: 'GAS',
          color: 'Yellow-green',
          odor: 'Swimming pool',
          persistence_minutes: 10,
          vapor_hazard: 'MODERATE'
        },
        onset_minutes: { irritation: 0.5 },
        lethality_untreated: 0.10,
        route: ['INHALATION'],
        notes: 'Syria barrel bombs. Immediate irritation.'
      }
    },
    
    treatment: {
      key: 'Enforce rest - activity worsens outcome',
      supportive: ['Oxygen', 'Bronchodilators', 'Steroids', 'Diuretics if edema'],
      monitoring: 'Watch for delayed pulmonary edema up to 72 hours'
    },
    
    educational: {
      key_point: 'Enforce rest - exertion worsens pulmonary edema',
      teaching_note: 'Phosgene smells like fresh hay - deceptively pleasant',
      common_mistake: 'Allowing activity in asymptomatic choking agent casualties'
    }
  }
};

// ============================================================================
// BIOLOGICAL AGENTS
// ============================================================================

export const BIOLOGICAL_AGENTS = {
  CATEGORY_A: {
    category: 'CATEGORY_A',
    label: 'Category A (High Priority)',
    description: 'Easily disseminated, high mortality, public panic',
    icon: ICONS.BIOHAZARD,
    color: '#F44336',
    
    agents: {
      ANTHRAX: {
        name: 'Anthrax (Bacillus anthracis)',
        icon: ICONS.BIOHAZARD,
        forms: ['Cutaneous', 'Inhalational', 'Gastrointestinal'],
        incubation_days: { min: 1, max: 60 },
        lethality: {
          inhalational_untreated: 0.95,
          inhalational_treated: 0.45,
          cutaneous_untreated: 0.20
        },
        treatment: {
          antibiotics: ['Ciprofloxacin', 'Doxycycline', 'Penicillin'],
          prophylaxis_days: 60
        },
        vaccine: 'BioThrax (AVA)',
        notes: 'Spores persist indefinitely. Decon essential.'
      },
      SMALLPOX: {
        name: 'Smallpox (Variola major)',
        icon: ICONS.BIOHAZARD,
        incubation_days: { min: 7, max: 17 },
        lethality_untreated: 0.30,
        transmission: 'Respiratory droplets, fomites',
        treatment: {
          supportive: true,
          antivirals: ['Tecovirimat (TPOXX)']
        },
        vaccine: 'ACAM2000 (within 3 days of exposure)',
        notes: 'Eradicated 1980. Lab stocks exist. Highly contagious.'
      },
      PLAGUE: {
        name: 'Plague (Yersinia pestis)',
        icon: ICONS.BIOHAZARD,
        forms: ['Bubonic', 'Septicemic', 'Pneumonic'],
        incubation_days: { min: 1, max: 6 },
        lethality: {
          pneumonic_untreated: 0.95,
          pneumonic_treated: 0.15
        },
        treatment: {
          antibiotics: ['Streptomycin', 'Gentamicin', 'Doxycycline'],
          duration_days: 10
        },
        notes: 'Pneumonic form transmissible. Treat within 24h.'
      },
      BOTULISM: {
        name: 'Botulism (Clostridium botulinum)',
        icon: ICONS.BIOHAZARD,
        incubation_hours: { min: 6, max: 36 },
        lethality: {
          untreated: 0.60,
          treated: 0.05
        },
        treatment: {
          antitoxin: 'Heptavalent Botulinum Antitoxin (HBAT)',
          supportive: 'Ventilatory support (may need weeks)'
        },
        notes: 'Descending paralysis. Antitoxin prevents progression.'
      }
    }
  },
  
  CATEGORY_B: {
    category: 'CATEGORY_B',
    label: 'Category B (Moderate Priority)',
    description: 'Moderate morbidity, lower mortality',
    icon: ICONS.BIOHAZARD,
    color: '#FF9800',
    
    agents: {
      Q_FEVER: {
        name: 'Q Fever (Coxiella burnetii)',
        incubation_days: { min: 14, max: 21 },
        lethality_untreated: 0.01,
        treatment: { antibiotics: ['Doxycycline'] }
      },
      BRUCELLOSIS: {
        name: 'Brucellosis',
        incubation_days: { min: 5, max: 60 },
        lethality_untreated: 0.02,
        treatment: { antibiotics: ['Doxycycline + Rifampin'] }
      },
      RICIN: {
        name: 'Ricin',
        onset_hours: 4,
        lethality_severe: 0.50,
        treatment: { supportive: true, antidote: 'None' }
      }
    }
  }
};

// ============================================================================
// RADIOLOGICAL
// ============================================================================

export const RADIOLOGICAL = {
  ACUTE_RADIATION_SYNDROME: {
    name: 'Acute Radiation Syndrome (ARS)',
    icon: ICONS.RADIATION,
    
    severity_by_dose: {
      MILD: {
        dose_gy: { min: 1, max: 2 },
        icon: ICONS.YELLOW,
        symptoms: 'Mild nausea/vomiting',
        prognosis: 'Survival likely without treatment',
        hospitalization: 'Outpatient'
      },
      MODERATE: {
        dose_gy: { min: 2, max: 4 },
        icon: ICONS.ORANGE,
        symptoms: 'Moderate GI, hematologic',
        prognosis: 'Survival probable with treatment',
        hospitalization: 'Required'
      },
      SEVERE: {
        dose_gy: { min: 4, max: 6 },
        icon: ICONS.RED,
        symptoms: 'Severe GI, hematologic, CNS',
        prognosis: 'Survival possible with aggressive care',
        hospitalization: 'ICU'
      },
      LETHAL: {
        dose_gy: { min: 6 },
        icon: ICONS.BLACK,
        symptoms: 'Multi-organ failure, CNS syndrome',
        prognosis: 'Death likely regardless of treatment',
        hospitalization: 'Expectant care'
      }
    }
  },
  
  TREATMENT: {
    CYTOKINES: {
      name: 'Colony Stimulating Factors',
      drugs: ['Filgrastim (G-CSF)', 'Sargramostim (GM-CSF)'],
      indication: 'Bone marrow suppression',
      icon: ICONS.SYRINGE
    },
    PRUSSIAN_BLUE: {
      name: 'Prussian Blue (Radiogardase)',
      indication: 'Cesium-137 or Thallium ingestion',
      dosing: '3g TID for 30 days',
      icon: ICONS.SYRINGE
    },
    DTPA: {
      name: 'DTPA (Ca/Zn-DTPA)',
      indication: 'Plutonium, Americium contamination',
      route: 'IV or nebulized',
      icon: ICONS.SYRINGE
    },
    KI: {
      name: 'Potassium Iodide',
      indication: 'Radioactive iodine exposure',
      dosing: '130mg daily',
      timing: 'Within 24 hours of exposure',
      icon: ICONS.SYRINGE
    }
  }
};

// ============================================================================
// DECONTAMINATION STANDARDS
// ============================================================================

export const DECONTAMINATION_STANDARDS = {
  PATIENT_DECON: {
    AMBULATORY: {
      type: 'Ambulatory Patient Decon',
      icon: ICONS.GREEN,
      throughput_per_hour: 40,
      staff_required: 4,
      water_liters_per_patient: 25,
      time_per_patient_minutes: 3,
      description: 'Walking wounded - self-assist'
    },
    NON_AMBULATORY: {
      type: 'Non-Ambulatory Patient Decon',
      icon: ICONS.RED,
      throughput_per_hour: 8,
      staff_required: 8,
      water_liters_per_patient: 50,
      time_per_patient_minutes: 15,
      description: 'Litter patients - full assistance'
    }
  },
  
  MASS_DECON: {
    EXPEDIENT: {
      type: 'Expedient Mass Decon',
      icon: ICONS.WARNING,
      method: 'Fire hose corridor',
      throughput_per_hour: 200,
      limitations: 'Less thorough, hypothermia risk',
      when_to_use: 'Mass casualty, limited resources'
    },
    TECHNICAL: {
      type: 'Technical Decon',
      icon: ICONS.SHIELD,
      method: 'Full decon station',
      throughput_per_hour: 60,
      requirements: ['Shelter', 'Hot water', 'Trained staff']
    }
  }
};

// ============================================================================
// PPE CAPABILITY IMPACTS
// ============================================================================

export const PPE_CAPABILITY_IMPACTS = {
  MOPP_0: {
    level: 0,
    description: 'Normal duty uniform',
    icon: ICONS.GREEN,
    medical_capability: 1.0,
    dexterity: 1.0,
    heat_stress: 'NONE'
  },
  MOPP_2: {
    level: 2,
    description: 'Overgarment, boots, mask carried',
    icon: ICONS.YELLOW,
    medical_capability: 0.90,
    dexterity: 0.95,
    heat_stress: 'MILD'
  },
  MOPP_4: {
    level: 4,
    description: 'Full protective ensemble',
    icon: ICONS.RED,
    medical_capability: 0.50,
    dexterity: 0.60,
    heat_stress: 'SEVERE',
    work_rest_cycles: {
      light_work: 50,
      moderate_work: 35,
      heavy_work: 20
    },
    notes: 'Medical procedures extremely difficult'
  }
};

// ============================================================================
// CBRN TRIAGE MODIFICATIONS
// ============================================================================

export const CBRN_TRIAGE_MODIFICATIONS = {
  MASS_CASUALTY: {
    description: 'CBRN creates mass casualty by default',
    icon: ICONS.WARNING,
    expectations: {
      t1_immediate: 0.15,
      t2_delayed: 0.25,
      t3_minimal: 0.50,
      t4_expectant: 0.10
    }
  },
  
  EXPECTANT_CRITERIA: {
    nerve_agent: {
      criteria: 'Severe exposure with seizures AND no antidote available',
      icon: ICONS.BLACK
    },
    radiation: {
      criteria: '>6 Gy whole body dose with GI and CNS symptoms',
      icon: ICONS.BLACK
    },
    biological: {
      criteria: 'Pneumonic plague >72h symptoms, respiratory failure',
      icon: ICONS.BLACK
    }
  }
};

// ============================================================================
// UKRAINE 2022-2024 CBRN CONTEXT
// ============================================================================

export const UKRAINE_CBRN_CONTEXT = {
  applicable: true,
  source: 'Ukraine 2022-2024 conflict assessment',
  icon: ICONS.WARNING,
  
  threat_assessment: {
    chemical: {
      level: 'MODERATE',
      agents_of_concern: ['Chlorine (improvised)', 'Sarin (strategic)', 'Novichok'],
      notes: 'Chlorine barrel bombs used in Syria template'
    },
    biological: {
      level: 'LOW',
      notes: 'Disinformation about US biolabs, no credible BW use'
    },
    radiological: {
      level: 'MODERATE',
      concerns: ['Zaporizhzhia NPP', 'Dirty bomb false flag', 'RNBC storage'],
      notes: 'Nuclear plant combat unprecedented'
    },
    nuclear: {
      level: 'LOW_MODERATE',
      notes: 'Tactical nuclear rhetoric from Russia'
    }
  },
  
  lessons_learned: [
    'Pre-position antidote stockpiles forward',
    'Train all medics on CBRN triage',
    'Decon capability essential at Role 2',
    'PPE degrades medical capability significantly'
  ]
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get chemical agent category options for UI dropdown
 * @returns {Array} Category options
 */
export const getChemicalCategoryOptions = () => {
  return Object.entries(CHEMICAL_AGENTS).map(([key, cat]) => ({
    value: key,
    label: cat.label,
    icon: cat.icon,
    color: cat.color,
    priority: cat.priority,
    shortLabel: cat.shortLabel
  }));
};

/**
 * Get specific chemical agent options for UI dropdown
 * @param {string} category - Agent category (NERVE, BLISTER, etc)
 * @returns {Array} Agent options
 */
export const getChemicalAgentOptions = (category) => {
  const cat = CHEMICAL_AGENTS[category];
  if (!cat?.agents) return [];
  
  return Object.entries(cat.agents).map(([key, agent]) => ({
    value: key,
    label: `${agent.nato_code} - ${agent.name}`,
    icon: agent.icon,
    natoCode: agent.nato_code,
    lethality: agent.lethality_untreated
  }));
};

/**
 * Get biological agent options for UI dropdown
 * @returns {Array} Bio agent options
 */
export const getBioAgentOptions = () => {
  const options = [];
  
  Object.entries(BIOLOGICAL_AGENTS).forEach(([catKey, cat]) => {
    Object.entries(cat.agents || {}).forEach(([key, agent]) => {
      options.push({
        value: key,
        label: agent.name,
        icon: cat.icon,
        category: catKey,
        color: cat.color
      });
    });
  });
  
  return options;
};

/**
 * Get CBRN threat level options for UI dropdown
 * @returns {Array} Threat level options
 */
export const getThreatLevelOptions = () => {
  return Object.entries(CBRN_THREAT_LEVELS).map(([key, level]) => ({
    value: key,
    label: level.label,
    icon: level.icon,
    color: level.color,
    moppLevel: level.mopp_level,
    medicalImpact: level.medical_impact
  }));
};

/**
 * Get MOPP level options for UI dropdown
 * @returns {Array} MOPP level options
 */
export const getMOPPOptions = () => {
  return Object.entries(PPE_CAPABILITY_IMPACTS).map(([key, level]) => ({
    value: level.level,
    label: `MOPP ${level.level}: ${level.description}`,
    icon: level.icon,
    medicalCapability: level.medical_capability,
    heatStress: level.heat_stress
  }));
};

/**
 * Get radiation dose severity options for UI dropdown
 * @returns {Array} Severity options
 */
export const getRadiationSeverityOptions = () => {
  return Object.entries(RADIOLOGICAL.ACUTE_RADIATION_SYNDROME.severity_by_dose).map(([key, sev]) => ({
    value: key,
    label: `${key}: ${sev.dose_gy.min}-${sev.dose_gy.max || '+'} Gy`,
    icon: sev.icon,
    prognosis: sev.prognosis
  }));
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get chemical agent by NATO code
 * @param {string} natoCode - NATO code (GA, GB, VX, etc)
 * @returns {Object|null} Agent data
 */
export const getAgentByNATOCode = (natoCode) => {
  for (const cat of Object.values(CHEMICAL_AGENTS)) {
    for (const agent of Object.values(cat.agents || {})) {
      if (agent.nato_code === natoCode) return agent;
    }
  }
  return null;
};

/**
 * Calculate CBRN casualties
 * @param {Object} params - Calculation parameters
 * @returns {Object} Casualty estimate
 */
export const calculateCBRNCasualties = (params = {}) => {
  const {
    agent_type = 'NERVE',
    exposure_level = 'MODERATE',
    exposed_personnel = 100,
    protection_level = 'NONE',
    antidote_available = true,
    decon_time_minutes = 30
  } = params;
  
  const agentCategory = CHEMICAL_AGENTS[agent_type];
  if (!agentCategory) {
    return { error: 'Invalid agent type', icon: ICONS.RED };
  }
  
  // Base casualty rate from clinical presentation (clinical_presentation reserved for future display use)
  const _presentation = agentCategory.clinical_presentation?.[exposure_level]; // eslint-disable-line no-unused-vars
  let baseRate = exposure_level === 'SEVERE' ? 0.80 : 
                 exposure_level === 'MODERATE' ? 0.50 : 0.20;
  
  // Protection modifier
  const protectionMod = protection_level === 'MOPP4' ? 0.10 :
                        protection_level === 'MOPP2' ? 0.40 : 1.0;
  
  // Antidote modifier (for nerve agents)
  const antidoteMod = antidote_available && agent_type === 'NERVE' ? 0.60 : 1.0;
  
  // Decon modifier
  const deconMod = decon_time_minutes < 2 ? 0.20 :
                   decon_time_minutes < 15 ? 0.60 : 1.0;
  
  const effectiveRate = baseRate * protectionMod * antidoteMod * deconMod;
  const totalCasualties = Math.round(exposed_personnel * effectiveRate);
  
  // Determine severity
  let icon = ICONS.GREEN;
  if (effectiveRate > 0.50) icon = ICONS.RED;
  else if (effectiveRate > 0.25) icon = ICONS.ORANGE;
  else if (effectiveRate > 0.10) icon = ICONS.YELLOW;
  
  return {
    agent_type: agentCategory.label,
    exposure_level,
    exposed_personnel,
    
    modifiers: {
      base_rate: baseRate,
      protection: protectionMod,
      antidote: antidoteMod,
      decontamination: deconMod
    },
    
    effective_rate: Math.round(effectiveRate * 100) / 100,
    total_casualties: totalCasualties,
    
    breakdown: {
      immediate: Math.round(totalCasualties * 0.30),
      delayed: Math.round(totalCasualties * 0.50),
      minimal: Math.round(totalCasualties * 0.20)
    },
    
    recommendations: [
      decon_time_minutes > 5 ? 'Expedite decontamination' : null,
      !antidote_available && agent_type === 'NERVE' ? 'CRITICAL: Obtain antidotes' : null
    ].filter(Boolean),
    
    icon
  };
};

/**
 * Calculate decontamination capacity
 * @param {Object} params - Decon parameters
 * @returns {Object} Capacity calculation
 */
export const calculateDeconCapacity = (params = {}) => {
  const {
    total_casualties,
    ambulatory_percent = 0.70,
    available_lanes = 4,
    available_staff = 20,
    water_available = true
  } = params;
  
  const ambulatory = Math.round(total_casualties * ambulatory_percent);
  const nonAmbulatory = total_casualties - ambulatory;
  
  const ambStandard = DECONTAMINATION_STANDARDS.PATIENT_DECON.AMBULATORY;
  const nonAmbStandard = DECONTAMINATION_STANDARDS.PATIENT_DECON.NON_AMBULATORY;
  
  const maxAmbLanes = Math.floor(available_staff / ambStandard.staff_required);
  const maxNonAmbLanes = Math.floor(available_staff / nonAmbStandard.staff_required);
  
  const effectiveAmbLanes = Math.min(available_lanes, maxAmbLanes);
  const effectiveNonAmbLanes = Math.min(Math.floor(available_lanes / 2), maxNonAmbLanes);
  
  const ambThroughput = effectiveAmbLanes * ambStandard.throughput_per_hour;
  const nonAmbThroughput = effectiveNonAmbLanes * nonAmbStandard.throughput_per_hour;
  
  const ambTimeHours = ambulatory / ambThroughput;
  const nonAmbTimeHours = nonAmbulatory / nonAmbThroughput;
  const totalTimeHours = Math.max(ambTimeHours, nonAmbTimeHours);
  
  const waterRequired = 
    (ambulatory * ambStandard.water_liters_per_patient) + 
    (nonAmbulatory * nonAmbStandard.water_liters_per_patient);
  
  let icon = ICONS.GREEN;
  if (totalTimeHours > 6) icon = ICONS.RED;
  else if (totalTimeHours > 4) icon = ICONS.ORANGE;
  else if (totalTimeHours > 2) icon = ICONS.YELLOW;
  
  return {
    input: { total_casualties, ambulatory, non_ambulatory: nonAmbulatory },
    capacity: {
      ambulatory_throughput_per_hour: ambThroughput,
      non_ambulatory_throughput_per_hour: nonAmbThroughput,
      effective_lanes: effectiveAmbLanes + effectiveNonAmbLanes
    },
    time_required: {
      ambulatory_hours: Math.round(ambTimeHours * 10) / 10,
      non_ambulatory_hours: Math.round(nonAmbTimeHours * 10) / 10,
      total_hours: Math.round(totalTimeHours * 10) / 10
    },
    resources: {
      water_liters_required: Math.round(waterRequired),
      water_available
    },
    icon
  };
};

/**
 * Get antidote protocol for specific agent
 * @param {string} agentCategory - Agent category
 * @param {string} severity - Severity level
 * @returns {Object} Antidote protocol
 */
export const getAntidoteProtocol = (agentCategory, severity = 'MODERATE') => {
  const categoryData = CHEMICAL_AGENTS[agentCategory];
  
  if (!categoryData?.treatment?.antidotes) {
    return { 
      available: false, 
      message: 'No specific antidote - supportive care',
      icon: ICONS.YELLOW
    };
  }
  
  const antidotes = categoryData.treatment.antidotes;
  const protocol = [];
  
  Object.entries(antidotes).forEach(([name, data]) => {
    const dosing = data.dosing?.[severity.toLowerCase()] || data.dosing?.all_severities;
    if (dosing) {
      protocol.push({
        medication: data.name,
        dose: dosing.dose_mg || dosing.dose_g,
        unit: dosing.dose_mg ? 'mg' : 'g',
        route: dosing.route,
        frequency: dosing.repeat_minutes ? `Every ${dosing.repeat_minutes} min` : 'Once',
        endpoint: data.endpoint,
        icon: data.icon
      });
    }
  });
  
  return {
    available: true,
    agent_category: agentCategory,
    severity,
    protocol,
    autoinjector: categoryData.treatment.autoinjectors,
    icon: ICONS.SYRINGE
  };
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate CBRN configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateCBRNConfiguration = (config = {}) => {
  const errors = [];
  const warnings = [];
  
  if (config.threat_level === 'HIGH' && !config.antidote_stockpile) {
    errors.push({
      code: 'NO_ANTIDOTE',
      message: 'High CBRN threat requires antidote stockpile',
      icon: ICONS.RED
    });
  }
  
  if (config.threat_level !== 'NONE' && !config.decon_capability) {
    errors.push({
      code: 'NO_DECON',
      message: 'CBRN threat without decontamination capability',
      icon: ICONS.RED
    });
  }
  
  if (config.chemical_threat && !config.mopp_available) {
    warnings.push({
      code: 'NO_MOPP',
      message: 'Chemical threat without MOPP gear',
      icon: ICONS.YELLOW
    });
  }
  
  if (config.biological_threat && !config.isolation_beds) {
    errors.push({
      code: 'NO_ISOLATION',
      message: 'Biological threat requires isolation capability',
      icon: ICONS.RED
    });
  }
  
  return { 
    valid: errors.length === 0,
    errors, 
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if agent category is valid
 * @param {string} category - Agent category
 * @returns {boolean} True if valid
 */
export const isValidAgentCategory = (category) => {
  return category in CHEMICAL_AGENTS;
};

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_CBRN_CONFIG = {
  chemical_threat: 'LOW',
  biological_threat: 'NONE',
  radiological_threat: 'NONE',
  nuclear_threat: 'NONE',
  antidote_stockpile: true,
  decon_capability: true,
  isolation_beds_percent: 10,
  mopp_available: true,
  icon: ICONS.YELLOW
};

export const HIGH_CBRN_THREAT_CONFIG = {
  chemical_threat: 'HIGH',
  biological_threat: 'MODERATE',
  radiological_threat: 'LOW',
  nuclear_threat: 'LOW',
  antidote_stockpile: true,
  decon_capability: true,
  isolation_beds_percent: 30,
  mopp_available: true,
  icon: ICONS.RED
};

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const CBRN_EDUCATIONAL = {
  key_principles: [
    'Decontamination BEFORE treatment (except life-saving)',
    'PPE reduces medical capability by 50% at MOPP 4',
    'Antidote timing critical - especially for nerve agents',
    'Mass casualty is the norm in CBRN',
    'Combined injuries (CBRN + trauma) have synergistic mortality'
  ],
  
  nerve_agent_pearls: [
    'SLUDGE: Salivation, Lacrimation, Urination, Defecation, GI, Emesis',
    'Atropine endpoint is DRY SECRETIONS not pupil size',
    'GD (Soman) ages in 2 MINUTES - 2-PAM must be immediate',
    'Convulsing patients ARE salvageable with immediate antidote',
    'May need 20-40mg atropine in severe cases'
  ],
  
  common_errors: [
    'Treating before decontaminating (contaminates facility)',
    'Using nitrite kit in smoke inhalation victims',
    'Stopping atropine because pupils dilated (wrong endpoint)',
    'Missing delayed-onset blister or choking casualties',
    'Allowing activity in choking agent victims'
  ]
};

// ============================================================================
// EXPORT ALL
// ============================================================================

// Named export object (import/no-anonymous-default-export compliance)
const cbrnMedicalModule = {
  CBRN_THREAT_LEVELS,
  CHEMICAL_AGENTS,
  BIOLOGICAL_AGENTS,
  RADIOLOGICAL,
  DECONTAMINATION_STANDARDS,
  PPE_CAPABILITY_IMPACTS,
  CBRN_TRIAGE_MODIFICATIONS,
  UKRAINE_CBRN_CONTEXT,
  CBRN_EDUCATIONAL,

  // UI Options
  getChemicalCategoryOptions,
  getChemicalAgentOptions,
  getBioAgentOptions,
  getThreatLevelOptions,
  getMOPPOptions,
  getRadiationSeverityOptions,

  // Helper functions
  getAgentByNATOCode,
  calculateCBRNCasualties,
  calculateDeconCapacity,
  getAntidoteProtocol,

  // Validation
  validateCBRNConfiguration,
  isValidAgentCategory,

  // Defaults
  DEFAULT_CBRN_CONFIG,
  HIGH_CBRN_THREAT_CONFIG
};

export default cbrnMedicalModule;
